import { useState } from 'react';

import type { StatPeriod, StatPoint } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';
import { barWidth, dateKeys, yearOptions } from './statShared';

/**
 * 주문 통계 / AS 통계 — 레거시 stat/order.blade.php · stat/as.blade.php 파리티.
 *
 * 두 화면은 축이 같다(일별/월별 접수·완료 + 팀 드릴다운). 차이는 호출 API 뿐이라
 * 하나의 컴포넌트로 그린다. 레거시 Morris 라인 차트는 차트 라이브러리 없이
 * 표 안의 CSS 막대로 대체한다.
 *
 * 그룹 드롭다운: 에너지공사(1,2)=보급 업체(회사) 목록, 보급업체(3,5)=팀 목록.
 * 팀별 접수/완료 표는 서버가 팀 카운트를 내려주는 3,5 에서만 그려진다.
 */
export function StatOrderAs({ kind }: { kind: 'order' | 'as' }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [period, setPeriod] = useState<StatPeriod>('day');
  const [groupId, setGroupId] = useState<number | ''>('');

  const stats = useAsync(() => {
    const params = {
      year,
      month,
      period,
      group_id: groupId === '' ? undefined : groupId,
    };
    return kind === 'order' ? api.orderStats(params) : api.asStats(params);
  }, [kind, year, month, period, groupId]);

  const data = stats.data;
  // 레거시처럼 기간 전체 날짜를 채워 빈 날짜는 0 으로 그린다.
  const byDate = new Map((data?.series ?? []).map((p) => [p.date, p]));
  const series: StatPoint[] = dateKeys(year, month, period).map(
    (date) => byDate.get(date) ?? { date, total: 0, completed: 0 },
  );
  const sumTotal = series.reduce((acc, p) => acc + p.total, 0);
  const sumCompleted = series.reduce((acc, p) => acc + p.completed, 0);
  const max = Math.max(...series.map((p) => p.total), ...series.map((p) => p.completed), 0);

  const groups = data?.groups ?? [];
  // 팀 카운트는 보급업체(3,5)에서만 내려온다 — 에너지공사 회사 목록은 드롭다운 전용.
  const countedGroups = groups.filter((g) => g.total_count !== null);

  return (
    <div className="admin__body admin__body--single">
      <section className="admin__list">
        <div className="toolbar">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}년
              </option>
            ))}
          </select>
          {period === 'day' && (
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          )}
          <select value={period} onChange={(e) => setPeriod(e.target.value as StatPeriod)}>
            <option value="day">일별</option>
            <option value="month">월별</option>
          </select>
          {groups.length > 0 && (
            <select
              value={groupId}
              onChange={(e) => setGroupId(e.target.value === '' ? '' : Number(e.target.value))}
            >
              <option value="">전체</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          )}
          <span className="stat-label">
            접수 {sumTotal.toLocaleString()}건 · 완료 {sumCompleted.toLocaleString()}건
          </span>
        </div>

        {stats.loading && <Spinner />}
        {stats.error && <ErrorBox message={stats.error} onRetry={stats.reload} />}

        {data && (
          <>
            {/* 상단 카드 — 기준 연도는 서버가 내려준 올해(레거시 파리티) */}
            <div className="cards cards--stat">
              <div className="card">
                <strong className="card__count">{data.total_count.toLocaleString()}건</strong>
                <span className="card__label">총 접수 건수 ({data.year}년)</span>
              </div>
              <div className="card">
                <strong className="card__count">{data.complete_count.toLocaleString()}건</strong>
                <span className="card__label">총 완료 건수 ({data.year}년)</span>
              </div>
            </div>

            <table className="grid stat-table">
              <thead>
                <tr>
                  <th>날짜</th>
                  <th className="num">접수 건수</th>
                  <th className="num">완료 건수</th>
                  <th className="bar-col" aria-label="막대 그래프" />
                </tr>
              </thead>
              <tbody>
                {series.map((p) => (
                  <tr key={p.date}>
                    <td>{p.date}</td>
                    <td className="num">{p.total.toLocaleString()} 건</td>
                    <td className="num">{p.completed.toLocaleString()} 건</td>
                    <td className="bar-col">
                      <span className="bar" style={{ width: barWidth(p.total, max) }} />
                      <span className="bar bar--done" style={{ width: barWidth(p.completed, max) }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {countedGroups.length > 0 && (
              <>
                {/* 팀 카운트가 내려오는 보급업체(3,5) 전용 — 레거시 상단 팀별 표 2개를 하나로 합쳤다 */}
                <h3 className="section">팀별 접수 · 완료 건수 ({data.year}년)</h3>
                <table className="grid stat-table">
                  <thead>
                    <tr>
                      <th>팀명</th>
                      <th className="num">접수 건수</th>
                      <th className="num">완료 건수</th>
                    </tr>
                  </thead>
                  <tbody>
                    {countedGroups.map((g) => (
                      <tr key={g.id}>
                        <td>{g.name}</td>
                        <td className="num">{(g.total_count ?? 0).toLocaleString()} 건</td>
                        <td className="num">{(g.complete_count ?? 0).toLocaleString()} 건</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </section>
    </div>
  );
}
